/**
 * Live Setup Cockpit boundary.
 *
 * Keep the browser deliberately boring: it composes the governed doors that
 * already own setup state, and never derives completion from an optimistic
 * draft.  That makes a refresh (or a second browser) the same experience rather
 * than a reset to a localStorage story.
 */
import type { SettingRow, SolumClient } from '@solum/sdk'
import type { InstanceType, SetupStatus } from './setup'

export interface DeploymentIdentity {
  id?: boolean
  instance_type: InstanceType
  name: string | null
  region: string | null
  created_at?: string
  updated_at?: string
}

export interface DeploymentHealthStatus {
  check_name: string
  instance_type: InstanceType
  status: 'ok' | 'blocker' | 'warning' | 'missing_policy'
  cutover_status: 'ok' | 'blocker' | 'review'
  blocking: boolean
  fresh: boolean
  detail: string
  policy?: { max_age_seconds: number; require_off_box: boolean; max_restore_drill_age_seconds?: number; rpo_target_seconds: number; rto_target_seconds: number }
  observation?: { observed_at: string; verdict: string; off_box: boolean; restore_drill_at?: string; restore_rto_seconds?: number }
}

/**
 * WHETHER THIS DEPLOYMENT CAN DELIVER AN INVITATION AT ALL (#1628).
 *
 * `portal.invitation_transport()` reads STORED SETTINGS only — it never probes
 * the mail endpoint, so `ok` means *configured*, never *healthy*, and the
 * cockpit must not let it read as the latter.
 *
 * It exists on the operator's host because the operator is who repairs it. The
 * customer administrator already sees the consequence in the portal's People
 * page ("Delivery pending" / "Needs attention" per invitation, #1628), and
 * `mb_onboarding.registration_readiness()` blocks OPENING registration without
 * it — but nothing showed the person holding the settings that the deployment
 * had drifted back to undeliverable after opening. That drift is the residual
 * gap #1628 was filed for.
 *
 * `reason` distinguishes the two nulls, because silence is what this whole
 * issue is about:
 *   · `not-installed` — no portal module, so there is nothing to say;
 *   · `not-visible`   — the door refused this reader. The section says so
 *                       rather than rendering a reassuring blank.
 */
export interface InvitationTransportStatus {
  check_name: string
  status: 'ok' | 'blocker'
  detail: string
}
export type InvitationTransport =
  | { known: true; item: InvitationTransportStatus }
  | { known: false; reason: 'not-installed' | 'not-visible' }

export interface LiveSetupSnapshot {
  identity: DeploymentIdentity
  status: SetupStatus
  currency: string | null
  installedModules: { name: string; version: string }[]
  backup: DeploymentHealthStatus
  invitationTransport: InvitationTransport
}

export const setupQueryKey = ['setup', 'live'] as const

export function explicitSetting(rows: SettingRow[], module: string, key: string): unknown {
  const row = rows.find((candidate) => candidate.module === module && candidate.key === key)
  // A declared default is useful in the picker, but it is not a deployment
  // choice. core.setup_status() uses the same distinction: only a persisted
  // core.setting row completes the step.
  return row?.is_set ? row.value : null
}

export async function loadLiveSetup(client: SolumClient): Promise<LiveSetupSnapshot> {
  const raw = client.raw()
  const [identity, status, settings, installedModules, backup] = await Promise.all([
    raw.rpcScalar<DeploymentIdentity>('deployment_identity', {}, 'core'),
    raw.rpcScalar<SetupStatus>('setup_status', {}, 'core'),
    client.describeSettings('platform'),
    raw.rpcTable<{ name: string; version: string }>('installed_modules', {}, 'core'),
    raw.rpcScalar<DeploymentHealthStatus>('deployment_health_status', { p_check_name: 'backup' }, 'core'),
  ])
  const currency = explicitSetting(settings, 'platform', 'base_currency')

  return {
    identity,
    status,
    currency: currency == null ? null : String(currency),
    installedModules,
    backup,
    invitationTransport: await loadInvitationTransport(client, installedModules),
  }
}

/**
 * Read the invitation-transport readiness item, when there is a portal to read
 * it for. Sequenced after `installed_modules` on purpose: calling a function
 * that does not exist is a 404 from PostgREST, and an outage-shaped error for a
 * module the deployment simply does not have is noise, not truth.
 */
export async function loadInvitationTransport(
  client: SolumClient,
  installedModules: { name: string }[],
): Promise<InvitationTransport> {
  if (!installedModules.some((m) => m.name === 'portal')) return { known: false, reason: 'not-installed' }
  try {
    const rows = await client.raw().rpcTable<InvitationTransportStatus>('invitation_transport', {}, 'portal')
    const item = rows[0]
    return item ? { known: true, item } : { known: false, reason: 'not-visible' }
  } catch {
    // The door admits service context, platform authority, or somebody who
    // administers a portal. A console reader who is none of those is told that,
    // rather than shown a blank that reads like "nothing to worry about".
    return { known: false, reason: 'not-visible' }
  }
}

export async function loadEnvironmentHealth(client: SolumClient): Promise<{ identity: DeploymentIdentity; backup: DeploymentHealthStatus }> {
  const raw = client.raw()
  const [identity, backup] = await Promise.all([
    raw.rpcScalar<DeploymentIdentity>('deployment_identity', {}, 'core'),
    raw.rpcScalar<DeploymentHealthStatus>('deployment_health_status', { p_check_name: 'backup' }, 'core'),
  ])
  return { identity, backup }
}

export async function saveDeploymentIdentity(
  client: SolumClient,
  identity: Pick<DeploymentIdentity, 'instance_type' | 'name' | 'region'>,
): Promise<void> {
  await client.raw().rpcScalar<void>('set_deployment_identity', {
    p_instance_type: identity.instance_type,
    p_name: identity.name?.trim() || null,
    p_region: identity.region?.trim() || null,
  }, 'core')
}

export async function saveBaseCurrency(client: SolumClient, currency: string): Promise<void> {
  if (!currency) throw new Error('Choose a base currency before saving.')
  await client.setSetting('platform', 'base_currency', currency)
}
